import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { TokenService } from './token.service';
import { environment } from '../../../environments/environment';
import { Message } from '../../pages/conversations/model/conversation.model';
import {
  MessageDeliveredEvent,
  MessageSeenEvent,
} from '../interfaces/message-receipt.interface';
import { ConversationAiHandoverUpdatedEvent } from '../models/ai-handover.model';
import { ConversationAiWorkflowUpdatedEvent } from '../models/ai-workflow.model';
import { NotificationItemType } from '../../pages/notifications/models/notification.model';
import { NetworkStatusNotifierService } from './network-status-notifier.service';
import { AccountStorageResetService } from './account-storage-reset.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket?: Socket;
  private readonly SERVER_URL = environment.socketUrl;
  private shouldConnect = false;
  private hasConnectedOnce = false;

  private isConnected$ = new BehaviorSubject<boolean>(false);
  private reconnect$ = new Subject<void>();

  constructor(
    private tokenService: TokenService,
    private networkStatusNotifier: NetworkStatusNotifierService,
    accountStorageReset: AccountStorageResetService,
  ) {
    accountStorageReset.reset$.subscribe(() => this.disconnect());
  }

  connect(): void {
    const token = this.tokenService.getToken();

    if (!token) {
      console.warn('Socket connection skipped: missing token');
      this.isConnected$.next(false);
      return;
    }

    this.shouldConnect = true;
    const socket = this.getSocket();

    if (socket.connected || socket.active) return;

    socket.connect();
  }

  getConnectionStatus(): Observable<boolean> {
    return this.isConnected$.asObservable();
  }

  onReconnect(): Observable<void> {
    return this.reconnect$.asObservable();
  }

  sendMessage(message: any): void {
    const requestId =
      typeof message?.requestId === 'string' && message.requestId.trim()
        ? message.requestId
        : this.createRequestId();

    this.getSocket().emit('newMessage', { ...message, requestId });
  }

  onMessage(): Observable<Message> {
    return fromEvent<Message>(this.getSocket(), 'newMessage');
  }

  onMessageDelivered(): Observable<MessageDeliveredEvent> {
    return fromEvent<MessageDeliveredEvent>(this.getSocket(), 'messageDelivered');
  }

  onMessageSeen(): Observable<MessageSeenEvent> {
    return fromEvent<MessageSeenEvent>(this.getSocket(), 'messageSeen');
  }

  /**
   * Acknowledges receipt of an incoming message so the sender gets their
   * "delivered" tick. Goes out on `sendMessage` — the only inbound message
   * event the gateway subscribes to. `message` and `requestId` must be
   * non-empty strings or the gateway's payload validation drops the ack.
   */
  ackMessageDelivered(messageId: string, conversationId: string): void {
    this.getSocket().emit('sendMessage', {
      type: 'ack',
      messageId,
      room: conversationId,
      message: 'ack',
      requestId: this.createRequestId(),
    });
  }

  /**
   * Marks the conversation as seen up to now (server clock). The server
   * broadcasts `messageSeen` to the room, flipping senders' ticks to ✓✓.
   */
  emitLastSeen(conversationId: string): void {
    this.getSocket().emit('updateLastSeen', { room: conversationId });
  }

  onDeleteMessage(): Observable<Message> {
    return fromEvent<Message>(this.getSocket(), 'deleteMessage');
  }

  onEditMessage(): Observable<Message> {
    return fromEvent<Message>(this.getSocket(), 'editMessage');
  }

  onNewConversation(): Observable<Message> {
    return fromEvent<Message>(this.getSocket(), 'newConversation');
  }

  onOrderUpdate(): Observable<any> {
    return fromEvent<any>(this.getSocket(), 'orderUpdate');
  }

  onConversationAiHandoverUpdated(): Observable<ConversationAiHandoverUpdatedEvent> {
    return fromEvent<ConversationAiHandoverUpdatedEvent>(
      this.getSocket(),
      'conversationAiHandoverUpdated',
    );
  }

  onConversationAiWorkflowUpdated(): Observable<ConversationAiWorkflowUpdatedEvent> {
    return fromEvent<ConversationAiWorkflowUpdatedEvent>(
      this.getSocket(),
      'conversationAiWorkflowUpdated',
    );
  }

  onNotification():Observable<string | NotificationItemType> {
    return fromEvent(this.getSocket(), 'notif');
  }

  onSystemNotification(): Observable<string | NotificationItemType> {
    return fromEvent(this.getSocket(), 'system-notif');
  }

  disconnect(): void {
    this.shouldConnect = false;

    if (this.socket) {
      this.socket.disconnect();
      this.isConnected$.next(false);
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  private getSocket(): Socket {
    if (!this.socket) {
      this.socket = io(this.SERVER_URL, {
        auth: (cb) => cb({ token: this.getAuthToken() }),
        transports: ['websocket', 'polling'],
        tryAllTransports: true,
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        randomizationFactor: 0.5,
        timeout: 10000,
      });

      this.bindSocketEvents(this.socket);
    }

    return this.socket;
  }

  private bindSocketEvents(socket: Socket): void {
    socket.on('connect', () => {
      const isReconnect = this.hasConnectedOnce;

      console.log('Socket connected');
      this.hasConnectedOnce = true;
      this.isConnected$.next(true);
      this.networkStatusNotifier.reportConnectionRecovered();

      if (isReconnect) {
        this.reconnect$.next();
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.isConnected$.next(false);

      if (this.shouldConnect) {
        // this.networkStatusNotifier.reportConnectionProblem(
        //   'ارتباط لحظه‌ای با سرور قطع شد. اینترنت، فیلترشکن یا پراکسی را بررسی کنید.'
        // );
      }

      if (reason === 'io server disconnect' && this.shouldConnect) {
        window.setTimeout(() => {
          if (this.shouldConnect && !socket.connected) {
            socket.connect();
          }
        }, 1000);
      }
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
      this.isConnected$.next(false);
      // if (this.shouldConnect) {
      //   this.networkStatusNotifier.reportConnectionProblem(
      //     'امکان اتصال لحظه‌ای به سرور وجود ندارد. اینترنت، فیلترشکن یا پراکسی را بررسی کنید.'
      //   );
      // }
    });

    socket.io.on('reconnect_attempt', (attemptNumber) => {
      console.log('Socket reconnect attempt:', attemptNumber);
    });

    socket.io.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after attempt:', attemptNumber);
    });

    socket.io.on('reconnect_error', (err) => {
      console.error('Socket reconnect error:', err.message);
    });

    socket.io.on('reconnect_failed', () => {
      console.error('Socket reconnect failed');
      this.isConnected$.next(false);
    });
  }

  private getAuthToken(): string {
    const token = this.tokenService.getToken();
    return token ? `Bearer ${token}` : '';
  }

  createRequestId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
